/**
 * URL helper functions
 */

/**
 * Transform site ID for API requests
 * @param siteId - The site ID to transform
 * @returns The transformed site ID
 */
export function transformSiteId(siteId: string): string {
  if (!siteId) {
    return '';
  }

  // Replace any special characters with underscores
  return siteId.replace(/[^a-zA-Z0-9]/g, '_');
}

/**
 * Get base URL for the current page
 * @returns The base URL
 */
export function getBaseUrl(): string {
  return window.location.protocol + '//' + window.location.host;
}

/**
 * Check if a URL is external
 * @param url - The URL to check
 * @returns True if the URL is external, false otherwise
 */
export function isExternalUrl(url: string): boolean {
  if (!url) {
    return false;
  }

  // If the URL starts with http:// or https:// and doesn't contain the current host
  if (
    (url.startsWith('http://') || url.startsWith('https://')) &&
    !url.includes(window.location.host)
  ) {
    return true;
  }

  return false;
}

/**
 * Get query parameters from URL
 * @param url - The URL to parse (optional, defaults to current URL)
 * @returns An object containing the query parameters
 */
export function getQueryParams(url?: string): Record<string, string> {
  const urlToUse = url || window.location.href;
  const queryString = urlToUse.split('?')[1];

  if (!queryString) {
    return {};
  }

  const params: Record<string, string> = {};
  const queryParams = queryString.split('&');

  for (const param of queryParams) {
    const [key, value] = param.split('=');
    params[decodeURIComponent(key)] = decodeURIComponent(value || '');
  }

  return params;
}

/**
 * Get a specific query parameter from URL
 * @param name - The name of the parameter to get
 * @param url - The URL to parse (optional, defaults to current URL)
 * @returns The value of the parameter, or null if not found
 */
export function getParam(name: string, url?: string): string | null {
  const params = getQueryParams(url);
  return params[name] || null;
}
